import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import LaptopIcon from '@mui/icons-material/Laptop';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import PublishIcon from '@mui/icons-material/Publish';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import LinkIcon from '@mui/icons-material/Link';
import QrCodeIcon from '@mui/icons-material/QrCode';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import { QRCodeSVG } from 'qrcode.react';
import {
  useDataProvider, useNotify, usePermissions, useRefresh,
} from 'react-admin';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import usePinguChat from '../../hooks/usePinguChat';
import useFormStudioEnabled from '../../hooks/useFormStudioEnabled';
import PinguComposer from '../pingu/PinguComposer';
import SelectInputButton from '../layout/inputFields/SelectInputButton';
import { formatFileSize, ImagePreviewField } from '../../customFields/EditImageField';
import PinguMessageList from '../pingu/PinguMessageList';
import { ChatSessionDetail } from '../pingu/types';
import { useRegisterBreadcrumb } from '../layout/navbar/BreadcrumbsManager';
import actions, { Actions } from '../../constants/actions';
import resourceNames from '../../constants/resourceNames';
import formStates, { FormStates } from '../../constants/formStates';
import hasAccess from '../../utilities/hasAccess';
import focusRing from '../../theme/focus';

type FormDefinition = {
  id: string;
  title: string;
  state?: FormStates;
  sourceFormId?: string;
  skipWelcomePage?: boolean;
  logo?: string | null;
  pages: { title: string; url: string; elements: unknown[] }[];
};

type PreviewSession = {
  previewToken: string;
  previewUrl: string;
  expiresInSeconds: number;
};

type TestLink = {
  url: string;
  expiresInMinutes: number;
};

type PreviewMessage = {
  type: string;
  location?: 'intro' | 'body' | 'end';
  page?: string;
};

type PreviewStop = {
  key: string;
  location: 'intro' | 'body' | 'end';
  page?: string;
  label: string;
  number?: number;
  disabled?: boolean;
};

type FrameSize = {
  width: number;
  height: number;
};

type LogoButtonPosition = {
  top: number;
  left: number;
};

type ResizeDirection = {
  x: -1 | 0 | 1;
  y: -1 | 0 | 1;
};

type FrameResizeHandle = ResizeDirection & {
  key: string;
  cursor: string;
  position: Record<string, number>;
};

const EDGE_GRAB = 8;
const CORNER_GRAB = 14;
const FRAME_RESIZE_HANDLES: FrameResizeHandle[] = [
  {
    key: 'top',
    x: 0,
    y: -1,
    cursor: 'ns-resize',
    position: {
      top: -EDGE_GRAB / 2, left: CORNER_GRAB / 2, right: CORNER_GRAB / 2, height: EDGE_GRAB,
    },
  },
  {
    key: 'bottom',
    x: 0,
    y: 1,
    cursor: 'ns-resize',
    position: {
      bottom: -EDGE_GRAB / 2, left: CORNER_GRAB / 2, right: CORNER_GRAB / 2, height: EDGE_GRAB,
    },
  },
  {
    key: 'left',
    x: -1,
    y: 0,
    cursor: 'ew-resize',
    position: {
      left: -EDGE_GRAB / 2, top: CORNER_GRAB / 2, bottom: CORNER_GRAB / 2, width: EDGE_GRAB,
    },
  },
  {
    key: 'right',
    x: 1,
    y: 0,
    cursor: 'ew-resize',
    position: {
      right: -EDGE_GRAB / 2, top: CORNER_GRAB / 2, bottom: CORNER_GRAB / 2, width: EDGE_GRAB,
    },
  },
  {
    key: 'top-left',
    x: -1,
    y: -1,
    cursor: 'nwse-resize',
    position: {
      top: -CORNER_GRAB / 2, left: -CORNER_GRAB / 2, width: CORNER_GRAB, height: CORNER_GRAB,
    },
  },
  {
    key: 'top-right',
    x: 1,
    y: -1,
    cursor: 'nesw-resize',
    position: {
      top: -CORNER_GRAB / 2, right: -CORNER_GRAB / 2, width: CORNER_GRAB, height: CORNER_GRAB,
    },
  },
  {
    key: 'bottom-left',
    x: -1,
    y: 1,
    cursor: 'nesw-resize',
    position: {
      bottom: -CORNER_GRAB / 2, left: -CORNER_GRAB / 2, width: CORNER_GRAB, height: CORNER_GRAB,
    },
  },
  {
    key: 'bottom-right',
    x: 1,
    y: 1,
    cursor: 'nwse-resize',
    position: {
      bottom: -CORNER_GRAB / 2, right: -CORNER_GRAB / 2, width: CORNER_GRAB, height: CORNER_GRAB,
    },
  },
];

const STAGE_PADDING = 16;
const WINDOW_BORDER = 1;
const BROWSER_BAR_HEIGHT = 40;
const MIN_FRAME_WIDTH = 320 + 2 * WINDOW_BORDER;
const MIN_FRAME_HEIGHT = 320 + BROWSER_BAR_HEIGHT + 2 * WINDOW_BORDER;
const MOBILE_FRAME_WIDTH = 390 + 2 * WINDOW_BORDER;
// The form app's md breakpoint, below which it switches to its mobile layout.
const FORM_MOBILE_LAYOUT_BELOW = 960;
const WINDOW_CONTROL_COLOURS = ['#ff5f57', '#febc2e', '#28c840'];
const SPLITTER_WIDTH = 1;
const SPLITTER_GRAB_MARGIN = 4;
const MIN_CHAT_WIDTH = 360;
const MIN_PREVIEW_WIDTH = MIN_FRAME_WIDTH + 2 * STAGE_PADDING;
const DEFAULT_CHAT_RATIO = 0.45;
const LOGO_ACCEPT = ['image/png', 'image/jpeg', 'image/webp'];
const LOGO_EXTENSIONS = '.png, .jpg, .jpeg, .webp';
const LOGO_MAX_SIZE = 1024 * 1024;

const FormStudioContent = (): React.ReactElement => {
  const { id = '' } = useParams<{ id: string }>();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const navigate = useNavigate();
  const routeLocation = useLocation();
  const { permissions } = usePermissions<Actions[]>();
  const canView = hasAccess(permissions, actions.FORM_STUDIO_ACCESS)
    || hasAccess(permissions, actions.FORM_GET);
  const canEdit = hasAccess(permissions, actions.FORM_STUDIO_ACCESS);
  const chat = usePinguChat();
  const { loadSession, startNewSession } = chat;

  const [form, setForm] = useState<FormDefinition | null>(null);
  const [nameRequired, setNameRequired] = useState(routeLocation.state?.focusFormName === true);
  const [editedTitle, setEditedTitle] = useState<string | null>(
    routeLocation.state?.focusFormName === true ? '' : null,
  );
  const [savingName, setSavingName] = useState(false);
  const [nameError, setNameError] = useState('');
  useRegisterBreadcrumb({ title: 'Forms', path: `/${resourceNames.FORMS}` });
  useRegisterBreadcrumb({
    title: form ? `${form.title} – Studio` : undefined,
    path: `/${resourceNames.FORMS}/${id}/studio`,
  });
  const [preview, setPreview] = useState<PreviewSession | null>(null);
  const [previewReady, setPreviewReady] = useState(false);
  const [previewLocation, setPreviewLocation] = useState<'intro' | 'body' | 'end'>('intro');
  const [previewPage, setPreviewPage] = useState<string | undefined>(undefined);
  const [previewLoading, setPreviewLoading] = useState(true);
  const [mobileTab, setMobileTab] = useState<'chat' | 'preview'>('chat');
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [logoDialogOpen, setLogoDialogOpen] = useState(false);
  const [logoFile, setLogoFile] = useState<{ file: File; url: string } | null>(null);
  const [logoError, setLogoError] = useState('');
  const [savingLogo, setSavingLogo] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const [stageSize, setStageSize] = useState<FrameSize | null>(null);
  const [frameSize, setFrameSize] = useState<FrameSize | null>(null);
  const [previewReloadCount, setPreviewReloadCount] = useState(0);
  const [testLink, setTestLink] = useState<TestLink | null>(null);
  const [creatingTestLink, setCreatingTestLink] = useState(false);
  const [viewportSize, setViewportSize] = useState<FrameSize | null>(null);
  const [resizingFrame, setResizingFrame] = useState(false);
  const [chatRatio, setChatRatio] = useState(DEFAULT_CHAT_RATIO);
  const [resizingPanels, setResizingPanels] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const panelsRef = useRef<HTMLDivElement>(null);
  const chatPanelRef = useRef<HTMLDivElement>(null);
  const panelResizeStart = useRef<{ x: number; chatWidth: number } | null>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLDivElement>(null);
  const [logoButtonPosition, setLogoButtonPosition] = useState<LogoButtonPosition | null>(null);
  const resizeStart = useRef<{
    x: number;
    y: number;
    size: FrameSize;
    direction: ResizeDirection;
  } | null>(null);
  const lastChatLoading = useRef(false);
  const locationToRestore = useRef<{ location: 'body' | 'end'; page?: string } | null>(null);
  const loadedSessionFor = useRef<string | null>(null);

  const createPreviewSession = useCallback(async (): Promise<void> => {
    const { data } = await dataProvider.create<PreviewSession>(
      `${resourceNames.FORMS}/${id}/preview-session`,
      { data: {} },
    );
    setPreview(data);
    setPreviewReady(false);
  }, [dataProvider, id]);

  // A separate session so the framed preview keeps its own token.
  const createTestLink = useCallback(async (): Promise<TestLink> => {
    const { data } = await dataProvider.create<PreviewSession>(
      `${resourceNames.FORMS}/${id}/preview-session`,
      { data: {} },
    );
    const url = new URL(data.previewUrl);
    // Only the Studio frame needs the form app's frame headers dropped.
    url.searchParams.delete('iframe');
    url.hash = `previewToken=${encodeURIComponent(data.previewToken)}`;
    return { url: url.toString(), expiresInMinutes: Math.round(data.expiresInSeconds / 60) };
  }, [dataProvider, id]);

  const copyTestLink = async (): Promise<void> => {
    setCreatingTestLink(true);
    const link = createTestLink();
    try {
      // Safari only allows the write during the click, so it starts before the link exists.
      await navigator.clipboard.write([new ClipboardItem({
        'text/plain': link.then(({ url }) => new Blob([url], { type: 'text/plain' })),
      })]);
      const { expiresInMinutes } = await link;
      notify(
        `Test link copied. Open it within ${expiresInMinutes} minutes; submitting it sends no data.`,
        { type: 'success' },
      );
    } catch {
      notify('The test link could not be copied.', { type: 'error' });
    } finally {
      setCreatingTestLink(false);
    }
  };

  const showTestLinkQrCode = async (): Promise<void> => {
    setCreatingTestLink(true);
    try {
      setTestLink(await createTestLink());
    } catch {
      notify('The test link could not be created.', { type: 'error' });
    } finally {
      setCreatingTestLink(false);
    }
  };

  const loadForm = useCallback(async (): Promise<FormDefinition> => {
    const { data } = await dataProvider.getOne<FormDefinition>(resourceNames.FORMS, { id });
    setForm(data);
    return data;
  }, [dataProvider, id]);

  useEffect(() => {
    let active = true;
    setPreviewLoading(true);
    loadForm().then(async (loadedForm) => {
      if (!active) return;
      if ((loadedForm.state ?? formStates.PUBLISHED) !== formStates.DRAFT) return;
      await createPreviewSession();

      if (loadedSessionFor.current === id) return;
      loadedSessionFor.current = id;
      startNewSession();
      const { data: sessions } = await dataProvider.getList<ChatSessionDetail>(
        resourceNames.PINGU_SESSIONS,
        {
          pagination: { page: 1, perPage: 1 },
          sort: { field: 'createdAt', order: 'DESC' },
          filter: { sessionType: 'formstudio', formId: id },
        },
      );
      if (!active) return;
      const latestSession = sessions[0];
      if (
        latestSession?.sessionType === 'formstudio'
        && latestSession.sessionTypeArgs?.formId === id
      ) {
        await loadSession(latestSession.id);
      }
    }).catch((error: unknown) => {
      notify(error instanceof Error ? error.message : 'The form could not be loaded.', { type: 'error' });
    }).finally(() => {
      if (active) setPreviewLoading(false);
    });
    return () => { active = false; };
  }, [startNewSession, loadSession, createPreviewSession, dataProvider, id, loadForm, notify]);

  useEffect(() => {
    const stage = stageRef.current;
    const viewport = viewportRef.current;
    if (!stage || !viewport) return () => {};
    const keepIfUnchanged = (next: FrameSize) => (current: FrameSize | null): FrameSize => (
      current?.width === next.width && current.height === next.height ? current : next
    );
    const observer = new ResizeObserver(() => {
      setStageSize(keepIfUnchanged({
        width: Math.floor(stage.clientWidth - 2 * STAGE_PADDING),
        height: Math.floor(stage.clientHeight - 2 * STAGE_PADDING),
      }));
      setViewportSize(keepIfUnchanged({
        width: Math.round(viewport.clientWidth),
        height: Math.round(viewport.clientHeight),
      }));
    });
    observer.observe(stage);
    observer.observe(viewport);
    return () => observer.disconnect();
  }, [form?.state]);

  // The shared composer has no slot for extra controls, so this sits beside its attach button.
  useEffect(() => {
    const composer = composerRef.current;
    if (!composer) return () => {};
    const placeLogoButton = (): void => {
      const attachButton = composer.querySelector<HTMLElement>('[aria-label="Attach files"]');
      const composerRect = composer.getBoundingClientRect();
      const attachRect = attachButton?.getBoundingClientRect();
      if (!attachRect?.width) {
        setLogoButtonPosition(null);
        return;
      }
      const top = Math.round(attachRect.top - composerRect.top);
      const left = Math.round(attachRect.right - composerRect.left + 4);
      setLogoButtonPosition((current) => (
        current?.top === top && current.left === left ? current : { top, left }
      ));
    };
    placeLogoButton();
    const observer = new ResizeObserver(placeLogoButton);
    observer.observe(composer);
    return () => observer.disconnect();
  }, [form?.state]);

  const clampFrameSize = useCallback((size: FrameSize): FrameSize => {
    const clamp = (value: number, min: number, max = value): number => (
      Math.round(Math.min(Math.max(value, min), max))
    );
    return {
      width: clamp(size.width, MIN_FRAME_WIDTH, stageSize?.width),
      height: clamp(size.height, MIN_FRAME_HEIGHT, stageSize?.height),
    };
  }, [stageSize]);

  const currentFrameSize = (): FrameSize => {
    const rect = frameRef.current?.getBoundingClientRect();
    return { width: rect?.width ?? 0, height: rect?.height ?? 0 };
  };

  const startFrameResize = (direction: ResizeDirection) => (
    event: React.PointerEvent<HTMLDivElement>,
  ): void => {
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    resizeStart.current = {
      x: event.clientX, y: event.clientY, size: currentFrameSize(), direction,
    };
    setResizingFrame(true);
  };

  // The window stays centred in the stage, so both opposite edges move together.
  const moveFrameResize = (event: React.PointerEvent<HTMLDivElement>): void => {
    const start = resizeStart.current;
    if (!start) return;
    setFrameSize(clampFrameSize({
      width: start.size.width + 2 * start.direction.x * (event.clientX - start.x),
      height: start.size.height + 2 * start.direction.y * (event.clientY - start.y),
    }));
  };

  const endFrameResize = (event: React.PointerEvent<HTMLDivElement>): void => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    resizeStart.current = null;
    setResizingFrame(false);
  };

  const resizeFrameWithKeyboard = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    const step = event.shiftKey ? 50 : 10;
    const deltas: Record<string, [number, number]> = {
      ArrowLeft: [-step, 0],
      ArrowRight: [step, 0],
      ArrowUp: [0, -step],
      ArrowDown: [0, step],
    };
    const delta = deltas[event.key];
    if (!delta) return;
    event.preventDefault();
    const size = currentFrameSize();
    setFrameSize(clampFrameSize({ width: size.width + delta[0], height: size.height + delta[1] }));
  };

  const displayedFrameSize = frameSize && stageSize ? clampFrameSize(frameSize) : null;

  const setChatWidth = (chatWidth: number): void => {
    const available = (panelsRef.current?.clientWidth ?? 0) - SPLITTER_WIDTH;
    if (available <= 0) return;
    const maxChatWidth = Math.max(MIN_CHAT_WIDTH, available - MIN_PREVIEW_WIDTH);
    setChatRatio(Math.min(Math.max(chatWidth, MIN_CHAT_WIDTH), maxChatWidth) / available);
  };

  const startPanelResize = (event: React.PointerEvent<HTMLDivElement>): void => {
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    panelResizeStart.current = {
      x: event.clientX,
      chatWidth: chatPanelRef.current?.clientWidth ?? 0,
    };
    setResizingPanels(true);
  };

  const movePanelResize = (event: React.PointerEvent<HTMLDivElement>): void => {
    const start = panelResizeStart.current;
    if (start) setChatWidth(start.chatWidth + event.clientX - start.x);
  };

  const endPanelResize = (event: React.PointerEvent<HTMLDivElement>): void => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    panelResizeStart.current = null;
    setResizingPanels(false);
  };

  const resizePanelsWithKeyboard = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    const step = event.shiftKey ? 100 : 20;
    const deltas: Record<string, number> = { ArrowLeft: -step, ArrowRight: step };
    const delta = deltas[event.key];
    if (delta === undefined) return;
    event.preventDefault();
    setChatWidth((chatPanelRef.current?.clientWidth ?? 0) + delta);
  };

  const getPreviewDevice = (): 'laptop' | 'mobile' => {
    if (!frameSize) return 'laptop';
    return (viewportSize?.width ?? 0) < FORM_MOBILE_LAYOUT_BELOW ? 'mobile' : 'laptop';
  };

  const selectPreviewDevice = (device: 'laptop' | 'mobile'): void => {
    if (device === 'laptop') setFrameSize(null);
    if (device === 'mobile') setFrameSize({ width: MOBILE_FRAME_WIDTH, height: Number.MAX_SAFE_INTEGER });
  };

  const reloadPreview = (): void => {
    locationToRestore.current = null;
    setPreviewLocation('intro');
    setPreviewPage(undefined);
    setPreviewReloadCount((count) => count + 1);
    createPreviewSession().catch(() => notify('Preview could not be reloaded.', { type: 'error' }));
  };

  const sendPreviewMessage = useCallback((message: PreviewMessage, origin: string): void => {
    iframeRef.current?.contentWindow?.postMessage(message, origin);
  }, []);

  // The form app returns to its intro page after a refresh, so the Studio navigates back.
  const refreshPreviewInPlace = useCallback((origin: string): void => {
    locationToRestore.current = previewLocation === 'intro'
      ? null
      : { location: previewLocation, page: previewPage };
    sendPreviewMessage({ type: 'PINGWIRE_PREVIEW_REFRESH' }, origin);
  }, [previewLocation, previewPage, sendPreviewMessage]);

  const navigatePreview = (location: 'intro' | 'body' | 'end', page?: string): void => {
    if (!preview || !form || (location === 'body' && !form.pages.length)) return;
    setPreviewLocation(location);
    setPreviewPage(page);
    sendPreviewMessage(
      { type: 'PINGWIRE_PREVIEW_NAVIGATE', location, page },
      new URL(preview.previewUrl).origin,
    );
  };

  const handlePreviewMessage = useCallback((event: MessageEvent<PreviewMessage>) => {
    const frame = iframeRef.current;
    if (!preview || !frame || event.source !== frame.contentWindow) return;
    const previewOrigin = new URL(preview.previewUrl).origin;
    if (event.origin !== previewOrigin || typeof event.data?.type !== 'string') return;

    if (event.data.type === 'PINGWIRE_PREVIEW_AUTH_REQUIRED') {
      createPreviewSession().catch(() => notify('Preview access could not be renewed.', { type: 'error' }));
      return;
    }
    if (event.data.type === 'PINGWIRE_PREVIEW_READY') {
      setPreviewReady(true);
      sendPreviewMessage(
        { type: 'PINGWIRE_PREVIEW_NAVIGATE', location: previewLocation, page: previewPage },
        previewOrigin,
      );
      return;
    }
    if (event.data.type === 'PINGWIRE_PREVIEW_LOCATION_CHANGED' && event.data.location) {
      const restore = locationToRestore.current;
      locationToRestore.current = null;
      if (restore && event.data.location === 'intro') {
        sendPreviewMessage({ type: 'PINGWIRE_PREVIEW_NAVIGATE', ...restore }, previewOrigin);
        return;
      }
      setPreviewLocation(event.data.location);
      setPreviewPage(event.data.page);
    }
  }, [createPreviewSession, notify, preview, previewLocation, previewPage, sendPreviewMessage]);

  useEffect(() => {
    window.addEventListener('message', handlePreviewMessage);
    return () => window.removeEventListener('message', handlePreviewMessage);
  }, [handlePreviewMessage]);

  const refreshAfterChat = useCallback(async (): Promise<void> => {
    try {
      const latestForm = await loadForm();
      if ((latestForm.state ?? formStates.PUBLISHED) !== formStates.DRAFT) return;
      if (!preview) return;
      const previewOrigin = new URL(preview.previewUrl).origin;
      if (previewReady) {
        refreshPreviewInPlace(previewOrigin);
      } else {
        await createPreviewSession();
      }
    } catch (error) {
      notify(error instanceof Error ? error.message : 'The saved draft could not be reloaded.', {
        type: 'error',
      });
    }
  }, [
    createPreviewSession, loadForm, notify,
    preview, previewReady, refreshPreviewInPlace,
  ]);

  useEffect(() => {
    if (chat.loading) {
      lastChatLoading.current = true;
      return;
    }
    if (lastChatLoading.current) {
      lastChatLoading.current = false;
      refreshAfterChat();
    }
  }, [chat.loading, refreshAfterChat]);

  const publish = async (): Promise<void> => {
    if (!form) return;
    try {
      await dataProvider.create(`${resourceNames.FORMS}/${id}/publish`, {
        data: {},
      });
      window.sessionStorage.removeItem(`form-studio-session:${id}`);
      notify('Form published.', { type: 'success' });
      navigate(`/${resourceNames.FORMS}/${id}`);
    } catch (error) {
      notify(error instanceof Error ? error.message : 'The form could not be published.', { type: 'error' });
      loadForm();
    } finally {
      setPublishDialogOpen(false);
    }
  };

  const saveName = async (): Promise<void> => {
    if (!form || !canEdit || savingName || chat.loading || editedTitle === null) return;
    const title = editedTitle.trim();
    if (!title) {
      setNameError('Enter a form name.');
      return;
    }
    if (title === form.title && !nameRequired) {
      setEditedTitle(null);
      setNameError('');
      return;
    }
    setSavingName(true);
    try {
      const { data } = await dataProvider.update<FormDefinition>(resourceNames.FORMS, {
        id,
        data: { title },
        previousData: form,
      });
      setForm(data);
      setEditedTitle(null);
      setNameRequired(false);
      setNameError('');
      refresh();
      if (routeLocation.state?.focusFormName) {
        navigate(routeLocation.pathname, { replace: true, state: { focusFormName: false } });
      }
      if (preview && previewReady) refreshPreviewInPlace(new URL(preview.previewUrl).origin);
    } catch {
      setNameError('The form name could not be saved.');
    } finally {
      setSavingName(false);
    }
  };

  const getLogoUrl = useCallback(async (): Promise<string> => {
    const { data } = await dataProvider.getOne(`${resourceNames.FORMS}/${id}`, { id: 'logo' });
    return data.downloadUrl as string;
  }, [dataProvider, id]);

  const selectLogoFile = (file: File | null): void => {
    if (logoFile) URL.revokeObjectURL(logoFile.url);
    setLogoFile(null);
    setLogoError('');
    if (!file) return;
    if (!LOGO_ACCEPT.includes(file.type)) {
      setLogoError(`Choose a ${LOGO_EXTENSIONS} image.`);
      return;
    }
    if (file.size > LOGO_MAX_SIZE) {
      setLogoError(`The image must be ${formatFileSize(LOGO_MAX_SIZE)} or smaller.`);
      return;
    }
    setLogoFile({ file, url: URL.createObjectURL(file) });
  };

  const closeLogoDialog = (): void => {
    if (savingLogo) return;
    selectLogoFile(null);
    setLogoDialogOpen(false);
  };

  const saveLogo = async (logo: { rawFile: File } | null): Promise<void> => {
    if (!form) return;
    setSavingLogo(true);
    try {
      const { data } = await dataProvider.update<FormDefinition>(resourceNames.FORMS, {
        id,
        data: { logo },
        previousData: form,
      });
      setForm(data);
      if (logoFile) URL.revokeObjectURL(logoFile.url);
      setLogoFile(null);
      setLogoError('');
      setLogoDialogOpen(false);
      notify(logo ? 'Logo saved.' : 'Logo removed.', { type: 'success' });
      if (preview && previewReady) refreshPreviewInPlace(new URL(preview.previewUrl).origin);
    } catch {
      setLogoError(logo ? 'The logo could not be saved.' : 'The logo could not be removed.');
    } finally {
      setSavingLogo(false);
    }
  };

  const cancelName = (): void => {
    setEditedTitle(null);
    setNameError('');
    setNameRequired(false);
    if (routeLocation.state?.focusFormName) {
      navigate(routeLocation.pathname, { replace: true, state: { focusFormName: false } });
    }
  };

  if (!canView) return <Alert severity="error">You do not have permission to view forms.</Alert>;
  if (!form && previewLoading) return <Typography>Loading form studio…</Typography>;
  if (!form) return <Alert severity="error">This form could not be loaded.</Alert>;
  if ((form.state ?? formStates.PUBLISHED) !== formStates.DRAFT) {
    return (
      <Alert severity="info" action={<Button onClick={() => navigate(`/${resourceNames.FORMS}`)}>Forms</Button>}>
        Studio edits drafts only. Create a draft revision to edit this form here.
      </Alert>
    );
  }

  const previewSource = preview
    ? `${preview.previewUrl}#previewToken=${encodeURIComponent(preview.previewToken)}`
    : undefined;
  const canPublish = hasAccess(permissions, actions.FORM_UPDATE)
    && form.pages.some(({ elements }) => elements.length > 0);
  const canEditLogo = hasAccess(permissions, actions.FORM_UPDATE);
  const previewStops: PreviewStop[] = [
    {
      key: 'intro', location: 'intro', label: 'Intro page', disabled: Boolean(form.skipWelcomePage),
    },
    ...form.pages.map((page, index): PreviewStop => ({
      key: `page:${page.url}`, location: 'body', page: page.url, label: page.title, number: index + 1,
    })),
    { key: 'end', location: 'end', label: 'End page' },
  ];
  const currentStopIndex = previewStops.findIndex(({ location, page }) => (
    location === previewLocation && (location !== 'body' || page === previewPage)
  ));
  const currentStop = previewStops[currentStopIndex] as PreviewStop | undefined;
  const enabledStops = previewStops.filter(({ disabled }) => !disabled);
  const previousStop = currentStopIndex === -1
    ? undefined
    : enabledStops.filter((stop) => previewStops.indexOf(stop) < currentStopIndex).pop();
  const nextStop = currentStopIndex === -1
    ? enabledStops[0]
    : enabledStops.find((stop) => previewStops.indexOf(stop) > currentStopIndex);
  // Hide the Previous / Next labels in a narrow preview column so the page title keeps room
  const pagerButtonSx = {
    flexShrink: 0,
    '@container (max-width: 480px)': {
      minWidth: 0,
      px: 0.75,
      '& .pager-label': { display: 'none' },
      '& .MuiButton-startIcon, & .MuiButton-endIcon': { mx: 0 },
    },
  };
  const renderStopLabel = (stop: PreviewStop): React.ReactElement => (
    <Box
      component="span"
      sx={{
        display: 'flex', alignItems: 'baseline', gap: 1, minWidth: 0,
      }}
    >
      {stop.number !== undefined && (
        <Typography
          component="span"
          variant="caption"
          color="text.secondary"
          sx={{ flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}
        >
          {`${stop.number}/${form.pages.length}`}
        </Typography>
      )}
      <Box
        component="span"
        title={stop.label}
        sx={{
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 320,
        }}
      >
        {stop.label}
      </Box>
    </Box>
  );

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      height: { xs: 'calc(100dvh - 144px)', md: 'calc(100vh - 176px)' },
      minHeight: 420,
      overflow: 'hidden',
    }}
    >
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        sx={{
          py: 2,
          px: 3,
          borderBottom: 1,
          borderColor: 'divider',
          flexWrap: 'wrap',
          rowGap: 1,
        }}
      >
        <Box sx={{ flex: '1 1 320px', minWidth: 0 }}>
          {editedTitle === null ? (
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="h6" noWrap sx={{ maxWidth: 480 }}>
                {form.title}
              </Typography>
              <Tooltip title="Edit form name">
                <IconButton
                  size="small"
                  aria-label="Edit form name"
                  disabled={!canEdit || chat.loading}
                  onClick={() => setEditedTitle(form.title)}
                >
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          ) : (
            <Stack direction="row" alignItems="center" spacing={1}>
              <TextField
                label="Form name"
                value={editedTitle}
                autoFocus
                required
                size="small"
                disabled={savingName}
                error={Boolean(nameError)}
                helperText={nameError || undefined}
                onChange={(event) => {
                  setEditedTitle(event.target.value);
                  setNameError('');
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    saveName();
                  }
                  if (event.key === 'Escape' && !savingName) cancelName();
                }}
                sx={{ width: 260, minWidth: 0 }}
              />
              <Button
                size="small"
                startIcon={<SaveOutlinedIcon fontSize="small" />}
                disabled={savingName}
                onClick={() => { saveName(); }}
              >
                Save
              </Button>
              <Tooltip title="Cancel name change">
                <IconButton
                  size="small"
                  aria-label="Cancel name change"
                  disabled={savingName}
                  onClick={cancelName}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          )}
        </Box>
        <Chip size="small" color="warning" variant="outlined" label="Draft" />
        <Button
          variant="contained"
          startIcon={<PublishIcon />}
          disabled={
            !canPublish || !canEdit || chat.loading || savingName || editedTitle !== null
          }
          onClick={() => setPublishDialogOpen(true)}
        >
          Publish
        </Button>
      </Stack>

      <Box sx={{ display: { xs: 'block', md: 'none' }, borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={mobileTab} onChange={(_, value: 'chat' | 'preview') => setMobileTab(value)}>
          <Tab value="chat" label="Chat" />
          <Tab value="preview" label="Preview" />
        </Tabs>
      </Box>

      <Box
        ref={panelsRef}
        sx={{
          display: 'grid',
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          gridTemplateColumns: {
            xs: 'minmax(0, 1fr)',
            md: `minmax(${MIN_CHAT_WIDTH}px, ${chatRatio}fr) ${SPLITTER_WIDTH}px minmax(0, ${1 - chatRatio}fr)`,
          },
          cursor: resizingPanels ? 'col-resize' : undefined,
          userSelect: resizingPanels ? 'none' : undefined,
        }}
      >
        <Box
          ref={chatPanelRef}
          sx={{
            display: { xs: mobileTab === 'chat' ? 'flex' : 'none', md: 'flex' }, flexDirection: 'column', minHeight: 0, height: '100%', overflow: 'hidden',
          }}
        >
          <PinguMessageList
            messages={chat.messages}
            loading={chat.loading || chat.loadingSession}
            activeSessionId={chat.sessionId}
            onMessageTyped={chat.markMessageTyped}
            onMessageFeedback={chat.submitMessageFeedback}
            onWithdrawFeedback={chat.withdrawMessageFeedback}
            onRetry={() => {
              if (canEdit) chat.retryLastMessage({ formId: id });
            }}
            onCheckAgain={() => { if (chat.sessionId) chat.loadSession(chat.sessionId); }}
          />
          <Box ref={composerRef} sx={{ position: 'relative' }}>
            <PinguComposer
              disabled={
                !canEdit || chat.loading || chat.loadingSession
                || savingName || editedTitle !== null
              }
              canCancel={chat.loading}
              onSend={(message, files) => chat.sendMessage(message, { formId: id }, files)}
              onCancel={() => { chat.cancelSession(); }}
            />
            {logoButtonPosition && (
              <Tooltip title="Form logo">
                <Box
                  component="span"
                  sx={{ position: 'absolute', top: logoButtonPosition.top, left: logoButtonPosition.left }}
                >
                  <IconButton
                    aria-label="Form logo"
                    disabled={!canEditLogo || chat.loading}
                    onClick={() => setLogoDialogOpen(true)}
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: 1.5,
                      color: 'text.secondary',
                      '&:focus-visible': { boxShadow: focusRing },
                    }}
                  >
                    <ImageOutlinedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              </Tooltip>
            )}
          </Box>
        </Box>

        <Box
          role="separator"
          tabIndex={0}
          aria-label="Resize chat and preview"
          aria-orientation="vertical"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(chatRatio * 100)}
          onPointerDown={startPanelResize}
          onPointerMove={movePanelResize}
          onPointerUp={endPanelResize}
          onPointerCancel={endPanelResize}
          onKeyDown={resizePanelsWithKeyboard}
          sx={{
            display: { xs: 'none', md: 'block' },
            position: 'relative',
            zIndex: 1,
            bgcolor: 'divider',
            cursor: 'col-resize',
            touchAction: 'none',
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: -SPLITTER_GRAB_MARGIN,
              right: -SPLITTER_GRAB_MARGIN,
            },
            '&:focus-visible': { outline: 'none', bgcolor: 'primary.main' },
          }}
        />

        <Box sx={{
          display: { xs: mobileTab === 'preview' ? 'flex' : 'none', md: 'flex' }, flexDirection: 'column', minHeight: 0, height: '100%', overflow: 'hidden',
        }}
        >
          <Stack spacing={1} sx={{ px: 2, py: 1.5 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
              sx={{ containerType: 'inline-size' }}
            >
              <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ width: 200, flexShrink: 0 }}>
                  <SelectInputButton
                    noWrap
                    variant="outlined"
                    sx={{ justifyContent: 'space-between' }}
                    label="Select a page"
                    disabled={!preview}
                    value={currentStop?.key}
                    setValue={(key: string) => {
                      const stop = previewStops.find((candidate) => candidate.key === key);
                      if (stop) navigatePreview(stop.location, stop.page);
                    }}
                    choices={previewStops.map((stop) => ({
                      id: stop.key,
                      name: stop.number === undefined
                        ? stop.label
                        : `${stop.number}/${form.pages.length} · ${stop.label}`,
                      custom: renderStopLabel(stop),
                      disabled: stop.disabled,
                    }))}
                  />
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<ChevronLeftIcon />}
                  aria-label="Previous page"
                  disabled={!preview || previousStop === undefined}
                  onClick={() => {
                    if (previousStop) navigatePreview(previousStop.location, previousStop.page);
                  }}
                  sx={pagerButtonSx}
                >
                  <span className="pager-label">Previous</span>
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  endIcon={<ChevronRightIcon />}
                  aria-label="Next page"
                  disabled={!preview || nextStop === undefined}
                  onClick={() => {
                    if (nextStop) navigatePreview(nextStop.location, nextStop.page);
                  }}
                  sx={pagerButtonSx}
                >
                  <span className="pager-label">Next</span>
                </Button>
                <Tooltip title="Copy test link">
                  <span>
                    <IconButton
                      size="small"
                      aria-label="Copy test link"
                      disabled={creatingTestLink}
                      onClick={() => { copyTestLink(); }}
                    >
                      <LinkIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Show test link QR code">
                  <span>
                    <IconButton
                      size="small"
                      aria-label="Show test link QR code"
                      disabled={creatingTestLink}
                      onClick={() => { showTestLinkQrCode(); }}
                    >
                      <QrCodeIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
                {viewportSize && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    aria-live="polite"
                    noWrap
                    sx={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    {`${viewportSize.width} × ${viewportSize.height} px`}
                  </Typography>
                )}
                <Tooltip title="Reload preview">
                  <span>
                    <IconButton
                      size="small"
                      aria-label="Reload preview"
                      disabled={!preview}
                      onClick={reloadPreview}
                    >
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  aria-label="Preview device"
                  value={getPreviewDevice()}
                >
                  <Tooltip title="Laptop">
                    <ToggleButton value="laptop" aria-label="Laptop" onClick={() => selectPreviewDevice('laptop')}>
                      <LaptopIcon fontSize="small" />
                    </ToggleButton>
                  </Tooltip>
                  <Tooltip title="Mobile">
                    <ToggleButton value="mobile" aria-label="Mobile" onClick={() => selectPreviewDevice('mobile')}>
                      <PhoneIphoneIcon fontSize="small" />
                    </ToggleButton>
                  </Tooltip>
                </ToggleButtonGroup>
              </Stack>
            </Stack>
          </Stack>
          <Divider />
          <Box
            ref={stageRef}
            sx={{
              flex: 1,
              minHeight: 0,
              p: `${STAGE_PADDING}px`,
              bgcolor: 'action.hover',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              overflow: 'hidden',
            }}
          >
            <Box sx={{
              position: 'relative', width: displayedFrameSize?.width ?? '100%', height: displayedFrameSize?.height ?? '100%', maxWidth: '100%', maxHeight: '100%',
            }}
            >
              <Box
                ref={frameRef}
                data-testid="respondent-browser-window"
                sx={{
                  width: '100%',
                  height: '100%',
                  border: WINDOW_BORDER,
                  borderColor: 'divider',
                  borderRadius: 2,
                  boxShadow: 6,
                  overflow: 'hidden',
                  bgcolor: 'background.paper',
                  boxSizing: 'border-box',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box
                  aria-hidden
                  sx={{
                    height: BROWSER_BAR_HEIGHT,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    px: 1.5,
                    bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'grey.900' : 'grey.200'),
                    borderBottom: 1,
                    borderColor: 'divider',
                  }}
                >
                  <Stack direction="row" spacing={0.75}>
                    {WINDOW_CONTROL_COLOURS.map((colour) => (
                      <Box
                        key={colour}
                        sx={{
                          width: 12, height: 12, borderRadius: '50%', bgcolor: colour,
                        }}
                      />
                    ))}
                  </Stack>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.75}
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      height: 26,
                      px: 1.5,
                      borderRadius: 13,
                      bgcolor: 'background.paper',
                      color: 'text.secondary',
                    }}
                  >
                    <LockOutlinedIcon sx={{ fontSize: 14 }} />
                    <Typography variant="caption" noWrap>{form.title}</Typography>
                  </Stack>
                </Box>
                <Box
                  ref={viewportRef}
                  data-testid="respondent-viewport"
                  sx={{ flex: 1, minHeight: 0, display: 'flex' }}
                >
                  {previewSource ? (
                    <Box
                      component="iframe"
                      key={`${previewSource}:${previewReloadCount}`}
                      ref={iframeRef}
                      title="Respondent form preview"
                      src={previewSource}
                      sandbox="allow-scripts allow-same-origin allow-forms"
                      referrerPolicy="no-referrer"
                      sx={{
                        flex: 1, width: '100%', height: '100%', border: 0, display: 'block', pointerEvents: resizingFrame || resizingPanels ? 'none' : 'auto',
                      }}
                    />
                  ) : (
                    <Box sx={{ flex: 1, display: 'grid', placeItems: 'center' }}>
                      <Typography color="text.secondary">Loading preview…</Typography>
                    </Box>
                  )}
                </Box>
              </Box>
              {FRAME_RESIZE_HANDLES.map((handle) => (
                <Box
                  key={handle.key}
                  aria-hidden
                  onPointerDown={startFrameResize(handle)}
                  onPointerMove={moveFrameResize}
                  onPointerUp={endFrameResize}
                  onPointerCancel={endFrameResize}
                  sx={{
                    position: 'absolute',
                    ...handle.position,
                    cursor: handle.cursor,
                    touchAction: 'none',
                    zIndex: 1,
                  }}
                />
              ))}
              <Box
                role="slider"
                tabIndex={0}
                aria-label="Resize respondent viewport"
                aria-valuetext={viewportSize ? `${viewportSize.width} by ${viewportSize.height} pixels` : undefined}
                onKeyDown={resizeFrameWithKeyboard}
                sx={{
                  position: 'absolute',
                  inset: 0,
                  borderRadius: 2,
                  pointerEvents: 'none',
                  '&:focus-visible': { outline: 2, outlineColor: 'primary.main', outlineOffset: 2 },
                }}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      <Dialog open={publishDialogOpen} onClose={() => setPublishDialogOpen(false)}>
        <DialogTitle>Publish this form?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The current draft will become available to respondents. You can continue editing the
            published form through the existing form settings.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPublishDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={publish}>Publish</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={logoDialogOpen} onClose={closeLogoDialog}>
        <DialogTitle>Logo</DialogTitle>
        <DialogContent>
          <Stack spacing={2} alignItems="flex-start" sx={{ minWidth: 280 }}>
            <Box sx={{ minHeight: 80, display: 'flex', alignItems: 'center' }}>
              {logoFile ? (
                <Box
                  component="img"
                  src={logoFile.url}
                  alt=""
                  sx={{ maxHeight: 80, maxWidth: 200, objectFit: 'contain' }}
                />
              ) : (
                <ImagePreviewField
                  imageKey={form.logo}
                  emptyText="No logo"
                  getPreviewUrl={getLogoUrl}
                />
              )}
            </Box>
            <input
              ref={logoInputRef}
              type="file"
              hidden
              accept={LOGO_ACCEPT.join(',')}
              onChange={(event) => {
                const input = event.currentTarget;
                selectLogoFile(input.files?.[0] ?? null);
                input.value = '';
              }}
            />
            <Button
              variant="outlined"
              size="small"
              disabled={savingLogo}
              onClick={() => logoInputRef.current?.click()}
            >
              Choose image
            </Button>
            <Typography variant="caption" color={logoError ? 'error' : 'text.secondary'}>
              {logoError || logoFile?.file.name || `${LOGO_EXTENSIONS} up to ${formatFileSize(LOGO_MAX_SIZE)}`}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          {form.logo && !logoFile && (
            <Button
              color="error"
              disabled={savingLogo}
              onClick={() => { saveLogo(null); }}
              sx={{ mr: 'auto' }}
            >
              Remove logo
            </Button>
          )}
          <Button disabled={savingLogo} onClick={closeLogoDialog}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!logoFile || savingLogo}
            onClick={() => { if (logoFile) saveLogo({ rawFile: logoFile.file }); }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={testLink !== null} onClose={() => setTestLink(null)}>
        <DialogContent>
          {testLink && <QRCodeSVG value={testLink.url} size={240} marginSize={2} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestLink(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const FormStudio = (): React.ReactElement => {
  const enabled = useFormStudioEnabled();
  const { permissions } = usePermissions<Actions[]>();
  if (!enabled) return <Alert severity="info">Form Studio is not enabled for this account.</Alert>;
  if (!hasAccess(permissions, actions.FORM_GET)
    || !hasAccess(permissions, actions.FORM_UPDATE)) {
    return <Alert severity="error">You do not have permission to edit forms.</Alert>;
  }
  return <FormStudioContent />;
};

export default FormStudio;
